`<template>
	<!-- 신규고객 등록 -->
	<div class="user_regist" v-show="isShow">
		<div v-if="isLoading" class="loading_bg round_none transparent" style="z-index: 999;">
			<div class="loading_wrap">
				<div class="loading_obj">
					<svg class="spinner" width="60px" height="60px" viewBox="0 0 66 66"
						xmlns="http://www.w3.org/2000/svg">
						<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30">
						</circle>
					</svg>
				</div>
			</div>
		</div>
		<!-- 등록할 때 -->
		<div class="regist_form" v-if="!isShowExist">
			<input type="text" :placeholder="$t('reservationwindow.reservationmonitor.register_patient_name')"
				class="input_xs w_full" v-model="name" />
			<div class="phone_input">
				<!-- <select class="input_s" v-model="countryCodeId" v-if="isWindow">
					<option v-for="(c, index) in countries" :key="index" :value="c.countryId">
						(+{{ c.internationalDialingCodes }}) {{ c.koreanCountryName }}
					</option>
				</select> -->
				<CountrySelectDropdown v-model="countryCodeId" :countries="countries" v-if="isWindow" />
				<PhoneInput :customClass="'input_xs'" v-model="phone" v-on:action_Done="action_Regist"
					:placeholder="$t('reservationwindow.reservationmonitor.register_patient_phone')"
					ref="input_phone" />
			</div>
			<div class="button_group">
				<button class="btn btn_s btn_dgray_line" @click="action_Cancel">{{ $t('common.cancel') }}</button>
				<button class="btn btn_s btn_blue" :disabled="!canRegist" @click="action_Regist">＋ {{
					$t('reservationwindow.reservationmonitor.register_patient_button_regist') }}</button>
			</div>
		</div>
		<!-- 이미 등록된 고객으로 실패했을 때 -->
		<ExistService :hservice="hservice" @setHService="setHService" @setSelectedService="setSelectedService" />
	</div>
</template>


<script>
import { deepCopy } from '@/util/deep-copy'
import { isValidPhone } from '@/util/validators'
import { useCountryCodes } from "@/composables/use-country-codes";
import PhoneInput from "@/v2/views/Main/ShareComponents/Common/PhoneInput.vue";
import CountrySelectDropdown from "@/v2/views/Main/V2ReservationManagement/Components/Common/CountrySelectDropdown.vue";
import { mapState } from "vuex";
import ExistService from "./ExistService.vue";
import { useQueryClient } from "@tanstack/vue-query";
import { hserviceFindSpecificQueryOptions } from "@/composables/queries/hservice/use-hservice-find-specific-query";
import { useRegistPatientCreateMutation } from "@/composables/queries/regist/use-regist-patient-create-mutation";

export default {
	setup() {
		const queryClient = useQueryClient();
		const { mutateAsync: registPatient } = useRegistPatientCreateMutation();
		const { koreaCountryCodeId, vietnamCountryCodeId } = useCountryCodes();
		return { queryClient, registPatient, koreaCountryCodeId, vietnamCountryCodeId };
	},
	components: { ExistService, PhoneInput, CountrySelectDropdown },
	props: {
		isShow: Boolean,
		newService: Object,
	},
	data() {
		return {
			hservice: undefined,
			name: "",
			// 초기값은 defaultCountryCodeID watch(immediate) 가 채운다
			countryCodeId: undefined,
			phone: "",
			isLoading: false,
		};
	},
	watch: {
		isShow: function (newValue) {
			if (newValue) {
				this.focusPhoneInput();
			} else {
				this.hservice = undefined;
			}
		},
		newService: {
			immediate: true,
			handler: function (newValue) {
				if (newValue == undefined) {
					return;
				}
				let obj = deepCopy(this.newService);
				this.name = obj.name;
				this.phone = obj.phone;
			},
		},
		defaultCountryCodeID: {
			immediate: true,
			handler: function (newValue) {
				this.countryCodeId = newValue;
			}
		}
	},
	computed: {
		...mapState(["SaaS"]),

		isWindow: function () {
			return this.SaaS?.isWindow ?? false;
		},
		countries() {
			const list = this.$store.state.servicesetting.countries;
			return Array.isArray(list) ? list : [];
		},
		isShowExist: function () {
			return this.hservice?._id != undefined;
		},
		hasName: function () {
			if (this.name == undefined) {
				return false;
			}
			return this.name.replaceAll(" ", "") != "";

		},
		hasPhone: function () {
			if (!this.phone) return false;

			if (this.isWindow) {
				return this.phone.length >= 4;
			}

			return isValidPhone(this.phone);
		},
		canRegist: function () {
			return this.hasName == true && this.hasPhone == true;
		},
		timezone: function () {
			return this.SaaS?.timezone ?? 'Asia/Seoul'
		},
		defaultCountryCodeID: function () {
			return this.timezone != 'Asia/Seoul' ? this.vietnamCountryCodeId : this.koreaCountryCodeId
		}
	},
	methods: {
		focusPhoneInput() {
			this.$nextTick(() => {
				let el = this.$refs.input_phone.$refs.tf_phone;
				if (el != undefined) {
					el.focus();
				}
			});
		},

		// MARK: - Actions
		async action_Regist() {
			let service = await this.loadHService();
			if (service != undefined) {
				this.hservice = service;
				return;
			}

			if (this.hservice == undefined) {
				await this.requestRegistPatient();
			}

			let hservice = deepCopy(this.hservice);
			delete hservice.isShowRegistHService;

			this.setSelectedService(hservice);
		},
		action_Cancel() {
			this.$emit("closeRegistService");
		},
		action_CloseExist() {
			this.hservice = undefined;
		},

		// MARK: - Network
		async loadHService() {
			const model = {
				hospitalID: this.hospital.selectedHospital._id,
				phone: this.phone.replaceAll("-", ""),
				isClosed: false,
				isTest: { $ne: true },
				...(this.isWindow && { countryCode: this.countryCodeId }),
			};
			// 봉투 에러(code != 0)는 ApiError로 throw — 기존의 throw Error(result) 와 동일하게 상위로 전파
			let services = await this.queryClient.fetchQuery(hserviceFindSpecificQueryOptions(model));
			if (services == undefined || services.length == 0) {
				return undefined;
			}
			return services.find(s => s.name === this.name);
		},

		async requestRegistPatient() {
			try {
				console.log(1111111);
				this.isLoading = true;
				let originMessage =
					this.hospital.selectedHospital?.smsReservationEdit ?? "";
				if (originMessage == undefined || originMessage == "") {
					originMessage = this.hospital.selectedHospital.smsReservation;
				}
				originMessage = originMessage.replace(
					"{{병원명}}",
					this.hospital.selectedHospital.shortName,
				);
				originMessage = originMessage.replace("{{고객명}}", this.name);
				let param = {
					name: this.name,
					phone: this.phone.replace(/[^0-9]/g, ""),
					type: 1000,
					hospitalID: this.hospital.selectedHospital._id,
					message: originMessage,
					...(this.isWindow && { countryCode: this.countryCodeId }),
				};
				// 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 throw Error(message) 동일)
				const registered = await this.registPatient(param);
				let hservices = registered.hservices;
				this.hservice = hservices[hservices.length - 1];
			} catch (err) {
				console.log("register patient error :", err);
			} finally {
				this.isLoading = false;
			}
		},

		// MARK: - Emit
		setHService(service) {
			this.hservice = service;
		},
		setSelectedService(service) {
			this.$emit("setSelectedService", service);
		},
	},
};
</script>`